import React, { useState, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid } from '@mui/material';
import { TECHNICAL_POLICY_COLUMNS } from '../../../../services/technicalPolicy/technicalPolicyColumns';
import { TwoActionsDialogService } from '../../../../services/twoActionsDialog/twoActionsDialogService';
import { WizardUtils } from '../../../../utils/WizardUtils';
import EditableTable from '../../components/EditableTable';
import GenericAutoCompleteInput from '../../components/GenericAutoCompleteInput';
import { useSequenceActions } from './hooks/SequenceActionsHook';

const SequencesActionsTab = ({ selectedSequenceAction, setSelectedSequenceAction, selectedGisement, editMode }) => {
  const { setValue, getValues, watch } = useFormContext();
  const {
    sequencesActions,
    coutsEtDurees,
    impacts,
    familleEquipement,
    niveauCout,
    tableNamesMapping,
    categoriesBudgetaires,
    tachesEnIntervention,
    tachesEnInterventionTable,
  } = useSequenceActions(selectedGisement);

  const watchedSequencesActions = watch(tableNamesMapping.SequencesActions);

  const sequenceActionOptions = useMemo(() => {
    const sequences = watchedSequencesActions?.map((sa) => sa.SequenceActions).filter(Boolean) || [];
    return WizardUtils.sortResourceListAlphabetically(Array.from(new Set(sequences)));
  }, [watchedSequencesActions]);

  const [openCoutDureeModal, setOpenCoutDureeModal] = useState(false);
  const [openImpactModal, setOpenImpactModal] = useState(false);
  const [openTacheEnInterventionModal, setOpenTacheEnInterventionModal] = useState(false);
  const [selectedActionId, setSelectedActionId] = useState('');
  const [updatedSequencesActions, setUpdatedSequencesActions] = useState(null);
  const openCoutsEtDureesModal = (actionId) => {
    setOpenCoutDureeModal(true);
    setSelectedActionId(actionId);
  };
  const openTachesEnInterventionModal = (actionId) => {
    setOpenTacheEnInterventionModal(true);
    setSelectedActionId(actionId);
  };

  const openImpactsModal = (actionId) => {
    setOpenImpactModal(true);
    setSelectedActionId(actionId);
  };

  const validateActionId = (row, allRows) => {
    if (!row.IdentifiantAction || row.IdentifiantAction.trim() === '') {
      return true;
    }
    return allRows?.some(
      (saRow) =>
        saRow.IdentifiantAction === row.IdentifiantAction &&
        saRow.SequenceActions === row.SequenceActions &&
        saRow.id !== row.id
    );
  };

  const parametresColumns = TECHNICAL_POLICY_COLUMNS.SequencesActions(
    (actionId) => openCoutsEtDureesModal(actionId),
    (actionId) => openTachesEnInterventionModal(actionId),
    (actionId) => openImpactsModal(actionId),
    categoriesBudgetaires,
    tachesEnIntervention,
    (row) =>
      sequencesActions?.some((saRow) => saRow.IdentifiantAction === row.IdentifiantAction && saRow.id === row.id),
    (row, allRows) => validateActionId(row, allRows)
  );

  const handleNewActionSaved = (newRow) => {
    if (!newRow?.IdentifiantAction) return;

    const currentData = updatedSequencesActions || sequencesActions || [];
    const updatedData = [...currentData, newRow];
    setUpdatedSequencesActions(updatedData);

    const existingImpacts = getValues(tableNamesMapping.Impacts) ?? impacts ?? [];
    const newImpacts = WizardUtils.generateDefaultImpacts(newRow.IdentifiantAction, familleEquipement);
    setValue(tableNamesMapping.Impacts, [...existingImpacts, ...newImpacts]);

    const couts = WizardUtils.generateDefaultCoutsEtDurees(
      newRow.NiveauDefinitionCouts,
      newRow.IdentifiantAction,
      familleEquipement,
      niveauCout
    );
    const existingCouts = getValues(tableNamesMapping.CoutsEtDureesActions) ?? coutsEtDurees ?? [];
    setValue(tableNamesMapping.CoutsEtDureesActions, [...existingCouts, ...couts]);
  };
  const handleAddSequenceAction = (newSequenceAction) => {
    setSelectedSequenceAction(newSequenceAction);
  };

  const handleDeleteSequenceAction = async (event) => {
    if (selectedSequenceAction) {
      event.stopPropagation();
      const dialogProps = {
        id: 'delete-sequence-action',
        labels: {
          title: "Supprimer la séquence d'actions ?",
          body:
            'Les actions, coûts et durées ainsi que les impacts associés à cette séquence ' +
            'seront également supprimés.',
          button1: 'Annuler',
          button2: 'Supprimer',
        },
      };
      const result = await TwoActionsDialogService.openDialog(dialogProps);
      if (result === 2) setSelectedSequenceAction('');
      return result;
    }
  };
  const handleActionRowUpdate = async (newRow, oldRow) => {
    if (newRow.IdentifiantAction && newRow.NiveauDefinitionCouts !== oldRow.NiveauDefinitionCouts) {
      const dialogResult = await TwoActionsDialogService.openDialog({
        id: 'confirm-niveau-definition-change',
        labels: {
          title: 'Changer le niveau de définition ?',
          body:
            'Cette modification entraînera la suppression des Coûts et Durées existants. ' +
            'De nouvelles valeurs par défaut seront générées.',
          button1: 'Annuler',
          button2: 'Continuer',
        },
      });
      if (dialogResult === 2) {
        const newCouts = WizardUtils.generateDefaultCoutsEtDurees(
          newRow.NiveauDefinitionCouts,
          newRow.IdentifiantAction,
          familleEquipement,
          niveauCout
        );
        const allCouts = getValues(tableNamesMapping.CoutsEtDureesActions) ?? coutsEtDurees ?? [];
        const filtered = allCouts.filter((c) => c.IdentifiantAction !== newRow.IdentifiantAction);
        setValue(tableNamesMapping.CoutsEtDureesActions, [...filtered, ...newCouts]);
      } else {
        const allRows = getValues(tableNamesMapping.SequencesActions) ?? [];
        const updatedRows = allRows.map((row) =>
          row.id === newRow.id ? { ...row, NiveauDefinitionCouts: oldRow.NiveauDefinitionCouts } : row
        );
        setValue(tableNamesMapping.SequencesActions, updatedRows);
      }
    }
  };

  const handleDeleteAction = (deletedRow) => {
    if (!deletedRow || !deletedRow.IdentifiantAction) return;

    const actionId = deletedRow.IdentifiantAction;

    if (updatedSequencesActions) {
      const filteredSequencesActions = updatedSequencesActions.filter((item) => item.IdentifiantAction !== actionId);
      setUpdatedSequencesActions(filteredSequencesActions.length > 0 ? filteredSequencesActions : null);
    }

    const updatedCoutsEtDurees = coutsEtDurees.filter((item) => item.IdentifiantAction !== actionId);

    const updatedImpacts = impacts.filter((item) => item.IdentifiantAction !== actionId);

    if (tableNamesMapping.CoutsEtDureesActions) {
      setValue(tableNamesMapping.CoutsEtDureesActions, updatedCoutsEtDurees);
    }

    if (tableNamesMapping.Impacts) {
      setValue(tableNamesMapping.Impacts, updatedImpacts);
    }
  };

  const handleValueCopyChange = (valueCopy) => {
    setUpdatedSequencesActions(valueCopy);
  };

  const confirmDeleteDialogLabels = {
    id: 'delete-actions-sequence',
    labels: {
      title: "Supprimer la séquence d'actions ?",
      body: 'Les coût et durées ainsi que les impacts associés à cette séquence seront également supprimés',
      button1: 'Annuler',
      button2: 'Supprimer',
    },
  };

  return (
    <Box width={'100%'} sx={{ padding: 2 }}>
      <Controller
        name={tableNamesMapping.SequencesActions}
        defaultValue={sequencesActions ?? []}
        render={({ field }) => {
          const { value, onChange } = field;
          return (
            <>
              <Grid container sx={{ gap: 1, marginBottom: 2 }}>
                <Grid item>
                  <GenericAutoCompleteInput
                    id="select-or-create-actions-sequence-autocomplete"
                    label={"Sélectionner ou créer une séquence d'actions"}
                    value={selectedSequenceAction || ''}
                    options={sequenceActionOptions}
                    onChange={(event, newSequenceAction) => setSelectedSequenceAction(newSequenceAction)}
                    onNewOption={handleAddSequenceAction}
                    editMode={editMode}
                  />
                </Grid>
                <Grid item>
                  {selectedSequenceAction && editMode && (
                    <IconButton
                      data-cy="delete-sequence-action-button"
                      onClick={async (event) => {
                        const response = await handleDeleteSequenceAction(event);
                        if (response === 2) {
                          const sequenceActionToDelete = selectedSequenceAction;

                          const sequencesActionsBeforeDeletion =
                            getValues(tableNamesMapping.SequencesActions) ?? sequencesActions ?? [];
                          const filteredSequencesActions = sequencesActionsBeforeDeletion.filter(
                            (sa) => sa.SequenceActions !== sequenceActionToDelete
                          );
                          onChange(filteredSequencesActions);

                          const actionsToDelete = sequencesActionsBeforeDeletion.filter(
                            (sa) => sa.SequenceActions === sequenceActionToDelete
                          );
                          const actionIdsToDelete = actionsToDelete.map((sa) => sa.IdentifiantAction);

                          const updatedCoutsEtDurees = (
                            getValues(tableNamesMapping.CoutsEtDureesActions) ??
                            coutsEtDurees ??
                            []
                          ).filter((item) => !actionIdsToDelete.includes(item.IdentifiantAction));

                          const updatedImpacts = (getValues(tableNamesMapping.Impacts) ?? impacts ?? []).filter(
                            (item) => !actionIdsToDelete.includes(item.IdentifiantAction)
                          );

                          if (tableNamesMapping.CoutsEtDureesActions) {
                            setValue(tableNamesMapping.CoutsEtDureesActions, updatedCoutsEtDurees);
                          }

                          if (tableNamesMapping.Impacts) {
                            setValue(tableNamesMapping.Impacts, updatedImpacts);
                          }
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
              <EditableTable
                key={selectedSequenceAction}
                id="actions-sequence"
                columns={parametresColumns.filter((col) => col.isInvisible !== true)}
                rows={value
                  .filter((action) => action.SequenceActions === selectedSequenceAction)
                  .sort((a, b) => (a.FamilleEquipementImpactee || '').localeCompare(b.FamilleEquipementImpactee || ''))}
                onChange={onChange}
                notFilteredRows={value}
                filteringCol={{ field: 'SequenceActions', value: selectedSequenceAction }}
                editMode={editMode && !!selectedSequenceAction}
                onDeleteRow={handleDeleteAction}
                confirmDeleteRow={true}
                confirmDeleteRowDialogProps={confirmDeleteDialogLabels}
                onNewRowSaved={handleNewActionSaved}
                addButtonAlignment="left"
                onRowUpdate={handleActionRowUpdate}
                onValueCopyChange={handleValueCopyChange}
              />
            </>
          );
        }}
      />

      {/* Modals for Coûts et Durées and Impacts */}
      <Dialog maxWidth={'xl'} fullWidth={true} open={openCoutDureeModal} onClose={() => setOpenCoutDureeModal(false)}>
        <DialogTitle>{'Coûts et Durées'}</DialogTitle>
        <DialogContent>
          <Controller
            name={tableNamesMapping.CoutsEtDureesActions}
            defaultValue={coutsEtDurees}
            render={({ field }) => {
              const { value, onChange } = field;
              const formActions = getValues(tableNamesMapping.SequencesActions) ?? [];
              const selectedAction = formActions.find((a) => a.IdentifiantAction === selectedActionId);
              const niveauDefinitionCout = selectedAction?.NiveauDefinitionCouts ?? '';

              return (
                <EditableTable
                  id="cost-and-duration"
                  columns={TECHNICAL_POLICY_COLUMNS.CoutsEtDureesActions(
                    niveauCout,
                    familleEquipement,
                    niveauDefinitionCout
                  ).filter((col) => col.isInvisible !== true)}
                  rows={value
                    ?.filter((row) => row.IdentifiantAction === selectedActionId)
                    .sort((a, b) => {
                      const familleCompare = (a.FamilleEquipement || '').localeCompare(b.FamilleEquipement || '');
                      if (familleCompare !== 0) return familleCompare;
                      return (a.NiveauCouts || '').localeCompare(b.NiveauCouts || '');
                    })}
                  onChange={onChange}
                  notFilteredRows={value}
                  filteringCol={{ field: 'IdentifiantAction', value: selectedActionId }}
                  editMode={editMode}
                  canAddDeleteRows={false}
                  inlineEdit={true}
                />
              );
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button data-cy="close-cost-and-duration-dialog-button" onClick={() => setOpenCoutDureeModal(false)}>
            {'Fermer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth={'xl'}
        fullWidth={true}
        open={openTacheEnInterventionModal}
        onClose={() => setOpenTacheEnInterventionModal(false)}
      >
        <DialogTitle>{'Tâches en Intervention'}</DialogTitle>
        <DialogContent>
          <Controller
            name={'tachesEnIntervention'}
            defaultValue={tachesEnInterventionTable}
            render={({ field }) => {
              const { value, onChange } = field;
              const filteredRows = value
                ?.filter((row) => {
                  return (updatedSequencesActions || sequencesActions)
                    ?.filter((sequenceAction) => sequenceAction.IdentifiantAction === selectedActionId)
                    .some((sequenceAction) => {
                      if (!sequenceAction.TachesEnIntervention) return false;
                      const tachesArray = sequenceAction.TachesEnIntervention.split('|');
                      return tachesArray.includes(row.Identifiant);
                    });
                })
                .sort((a, b) => (a.Identifiant || '').localeCompare(b.Identifiant || ''));

              return (
                <EditableTable
                  id="taches-en-intervention"
                  columns={TECHNICAL_POLICY_COLUMNS.TachesEnIntervention(familleEquipement).filter(
                    (col) => col.isInvisible !== true
                  )}
                  rows={filteredRows}
                  notFilteredRows={value}
                  filteringCol={{ field: 'IdentifiantAction', value: selectedActionId }}
                  onChange={(newData) => {
                    onChange(newData);
                  }}
                  canAddDeleteRows={false}
                  hideActions={true}
                />
              );
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button data-cy="close-impacts-dialog-button" onClick={() => setOpenTacheEnInterventionModal(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog maxWidth={'xl'} fullWidth={true} open={openImpactModal} onClose={() => setOpenImpactModal(false)}>
        <DialogTitle>{'Impacts'}</DialogTitle>
        <DialogContent>
          <Controller
            name={tableNamesMapping.Impacts}
            defaultValue={impacts}
            render={({ field }) => {
              const { value, onChange } = field;
              const handleImpactChange = (newValue) => {
                const oldRowsById = new Map(value?.map((r) => [r.id, r]) ?? []);
                const newRowsById = new Map(newValue.map((r) => [r.id, r]));

                const updatedValue = value.map((row) => {
                  const newRow = newRowsById.get(row.id);
                  // If the row is not in newValue, keep it as is
                  if (!newRow) return row;

                  const oldRow = oldRowsById.get(row.id);
                  // If nothing changed, return the same reference (optimization)
                  if (oldRow && oldRow.Type === newRow.Type && oldRow === newRow) return row;

                  const typeChanged = oldRow && oldRow.Type !== newRow.Type;
                  const newImpactPriorisation = typeChanged ? newRow.Type !== 'NoAction' : newRow.ImpactPriorisation;

                  if (newRow.Type === 'NoAction') {
                    return {
                      ...newRow,
                      Impact: 0,
                      FamilleEquipementRetrofit: null,
                      ImpactPriorisation: newImpactPriorisation,
                    };
                  } else if (
                    newRow.Type === 'Inspection' ||
                    newRow.Type === 'Activation' ||
                    newRow.Type === 'Deactivation'
                  ) {
                    return {
                      ...newRow,
                      Impact: 0,
                      FamilleEquipementRetrofit: null,
                      ImpactPriorisation: newImpactPriorisation,
                    };
                  } else if (newRow.Type === 'Replacement') {
                    return {
                      ...newRow,
                      Impact: 1,
                      ImpactPriorisation: newImpactPriorisation,
                    };
                  } else if (newRow.Type === 'Repairs' || newRow.Type === 'RepairsWithoutInspection') {
                    return {
                      ...newRow,
                      FamilleEquipementRetrofit: null,
                      ImpactPriorisation: newImpactPriorisation,
                    };
                  } else {
                    return {
                      ...newRow,
                      ImpactPriorisation: newImpactPriorisation,
                    };
                  }
                });
                onChange(updatedValue);
              };
              return (
                <EditableTable
                  id="impacts"
                  columns={TECHNICAL_POLICY_COLUMNS.Impacts(familleEquipement).filter(
                    (col) => col.isInvisible !== true
                  )}
                  rows={value
                    ?.filter((row) => row.IdentifiantAction === selectedActionId)
                    .sort((a, b) =>
                      (a.FamilleEquipementImpactee || '').localeCompare(b.FamilleEquipementImpactee || '')
                    )}
                  notFilteredRows={value}
                  filteringCol={{ field: 'IdentifiantAction', value: selectedActionId }}
                  onChange={handleImpactChange}
                  canAddDeleteRows={false}
                  inlineEdit={true}
                />
              );
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button data-cy="close-impacts-dialog-button" onClick={() => setOpenImpactModal(false)}>
            Fermer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

SequencesActionsTab.propTypes = {
  selectedSequenceAction: PropTypes.string,
  setSelectedSequenceAction: PropTypes.func.isRequired,
  selectedGisement: PropTypes.string,
  editMode: PropTypes.bool,
};

export default SequencesActionsTab;
